import Icon from "@/components/Icon/Icon";
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback";
import { cn } from "@/lib/cn";
import { defaultNS, useTranslation } from "@/lib/i18n";
import type { OnChangeFn } from "@dresscode-frontend/utility-types";
import {
  type ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useState,
} from "react";

type Props = {
  value?: string;
  onChange: OnChangeFn<string | undefined>;
  placeholder?: string;
  className?: string;
};

export const DeviceSearchInput = forwardRef<HTMLInputElement, Props>(
  ({ value, onChange, placeholder, className }, ref) => {
    const { t } = useTranslation(defaultNS, {
      keyPrefix: "domain.device.components.deviceFilters.search",
    });

    // ローカルステートで入力値を管理（デバウンスのため）
    const [inputValue, setInputValue] = useState(value ?? "");

    // propsのvalueが外部から変更された場合、ローカルステートを同期
    useEffect(() => {
      setInputValue(value ?? "");
    }, [value]);

    // onChangeをデバウンス（250ms）
    const debouncedOnChange = useDebouncedCallback(
      (newValue: string | undefined) => {
        onChange(newValue);
      },
      [onChange],
    );

    const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue); // ローカルステートを即座に更新
      debouncedOnChange(newValue || undefined); // デバウンスされたコールバックを実行
    }, []);

    const handleClear = useCallback(() => {
      setInputValue(""); // ローカルステートをクリア
      onChange(undefined); // 即座にonChangeを呼び出す（クリアは遅延不要）
    }, [onChange]);

    return (
      <div
        className={cn(
          "inline-flex h-8 w-44 items-center gap-1.5 overflow-hidden rounded-md bg-background px-3",
          "shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-border",
          "has-[input:focus]:ring-1 has-[input:focus]:ring-ring",
          className,
        )}
      >
        <Icon
          iconName="SearchIcon"
          className="h-3.5 w-3.5 text-muted-foreground"
        />
        <input
          ref={ref}
          type="text"
          value={inputValue}
          onChange={handleChange}
          placeholder={placeholder ?? t("placeholder")}
          className="flex-1 text-xs leading-4 outline-none line-clamp-1 placeholder:text-muted-foreground"
        />
        {inputValue && (
          <button
            type="button"
            onClick={handleClear}
            className="text-muted-foreground hover:text-foreground"
            aria-label={t("clear")}
          >
            <Icon iconName="XIcon" className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  },
);

DeviceSearchInput.displayName = "DeviceSearchInput";
